"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FINANCIAL_BILLING_REQUEST_EXAMPLES = exports.processInsuranceBillingRequestSchema = exports.processCorporateBillingRequestSchema = exports.billAndCreatePaymentRequestSchema = exports.billAgainstAdvanceRequestSchema = exports.computeChargeBillingRequestSchema = void 0;
/**
 * Request contracts for the 5 Billing endpoints —
 * `BillingApplicationService.computeChargeBilling`/`.billAgainstAdvance`/
 * `.billAndCreatePayment`/`.processCorporateBilling`/`.processInsuranceBilling`
 * (`backend/src/modules/financial/application/billing.application-service.ts`,
 * frozen, Stage 5C.3) — the top-level Charge → Discount → GST workflow
 * composer. `computeChargeBilling`'s result is a computed
 * `ChargeBillingSummaryDto`, never a persisted "Invoice" (no such
 * entity exists — ADR-017).
 *
 * ECM Phase 4: `processCorporateBilling`/`processInsuranceBilling` still
 * reuse `billAndCreatePaymentRequestSchema` verbatim, but the reason has
 * changed. They used to require the caller to supply the payer-appropriate
 * `tariffId` because no service could look one up; now they take `payerId`
 * and `RateResolutionService` resolves the applicable tariff through the
 * real hierarchy. The two methods stay thin wrappers because they genuinely
 * are the same workflow — the difference is data (which payer), not logic.
 */
const zod_1 = require("zod");
const financial_common_request_1 = require("./financial-common.request");
const charge_price_version_schema_1 = require("../../schemas/charge-price-version.schema");
/**
 * ECM Phase 4 — the pricing context every billing endpoint shares.
 *
 * `tariffId` is GONE, replaced by `payerId`. Making the caller name the rate
 * card was the defect, not the interface: nothing verified that the chosen
 * tariff was the right one for that payer, or even active. ECM now resolves it
 * through the real hierarchy (Package → Corporate → Insurance → Conditional →
 * Cash → Standard).
 *
 * `gstRatePercent` is now OPTIONAL and is a fallback only — ECM resolves tax
 * from the charge's price version. It is retired in Phase 6.
 *
 * Safe to change outright rather than deprecate: `fin_tariffs`,
 * `fin_invoice_lines` and priced charges were all confirmed empty in every
 * environment, so no live bill depends on the old shape.
 */
const chargePricingContextShape = {
    chargeId: zod_1.z.string().uuid(),
    quantity: zod_1.z.number().int().positive().optional(),
    payerId: zod_1.z.string().uuid().optional(),
    packageId: zod_1.z.string().uuid().optional(),
    /** Re-bill or credit-note an old invoice at the price that was in effect then. */
    asOfDate: zod_1.z.coerce.date().optional(),
    encounterContext: charge_price_version_schema_1.encounterContextSchema.optional(),
    discountRuleId: zod_1.z.string().uuid().optional(),
    gstRatePercent: zod_1.z.number().min(0).max(100).optional(),
    isInterState: zod_1.z.boolean(),
};
exports.computeChargeBillingRequestSchema = zod_1.z.object({
    ...chargePricingContextShape,
    ...financial_common_request_1.financialWorkflowExtrasShape,
});
exports.billAgainstAdvanceRequestSchema = zod_1.z.object({
    accountId: zod_1.z.string().uuid(),
    ...chargePricingContextShape,
    ...financial_common_request_1.financialWorkflowExtrasShape,
});
exports.billAndCreatePaymentRequestSchema = exports.computeChargeBillingRequestSchema.extend({
    branchId: zod_1.z.string().uuid(),
    patientId: zod_1.z.string().uuid().optional(),
    paymentModeId: zod_1.z.string().uuid(),
});
exports.processCorporateBillingRequestSchema = exports.billAndCreatePaymentRequestSchema;
exports.processInsuranceBillingRequestSchema = exports.billAndCreatePaymentRequestSchema;
exports.FINANCIAL_BILLING_REQUEST_EXAMPLES = {
    computeChargeBilling: {
        chargeId: 'cccccccc-cccc-cccc-cccc-cccccccccccc',
        quantity: 1,
        discountRuleId: 'ffffffff-ffff-ffff-ffff-ffffffffffff',
        isInterState: false,
    },
    billAgainstAdvance: {
        accountId: '66666666-6666-6666-6666-666666666666',
        chargeId: 'cccccccc-cccc-cccc-cccc-cccccccccccc',
        isInterState: false,
    },
    billAndCreatePayment: {
        chargeId: 'cccccccc-cccc-cccc-cccc-cccccccccccc',
        isInterState: false,
        branchId: '33333333-3333-3333-3333-333333333333',
        patientId: '44444444-4444-4444-4444-444444444444',
        paymentModeId: 'dddddddd-dddd-dddd-dddd-dddddddddddd',
    },
    processCorporateBilling: {
        chargeId: 'cccccccc-cccc-cccc-cccc-cccccccccccc',
        payerId: '88888888-8888-8888-8888-888888888888',
        isInterState: false,
        branchId: '33333333-3333-3333-3333-333333333333',
        paymentModeId: 'dddddddd-dddd-dddd-dddd-dddddddddddd',
    },
    processInsuranceBilling: {
        chargeId: 'cccccccc-cccc-cccc-cccc-cccccccccccc',
        payerId: '88888888-8888-8888-8888-888888888888',
        isInterState: false,
        branchId: '33333333-3333-3333-3333-333333333333',
        paymentModeId: 'dddddddd-dddd-dddd-dddd-dddddddddddd',
    },
};
//# sourceMappingURL=financial-billing.request.js.map